import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Drawer from './Drawer';
import { track, EVENTS } from '../utils/analytics';

export interface NavItem {
  to: string;
  label: string;
}

export const NAV: readonly NavItem[] = [
  { to: '/marketing', label: 'Ads' },
  { to: '/team', label: 'Your team' },
  { to: '/work', label: 'Work' },
];

const Header: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [stuck, setStuck] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (to: string) => !to.includes('#') && location.pathname === to;

  const cta = (where: string, onClick?: () => void) => (
    <Link
      to="/contact"
      className="button button--primary button--sm"
      onClick={() => {
        track(EVENTS.ctaClicked, { location: where, label: 'Get a free audit' });
        onClick?.();
      }}
    >
      Get a free audit
    </Link>
  );

  return (
    <>
      <header className="site-header" data-stuck={stuck}>
        <a className="skip-link" href="#main">
          Skip to content
        </a>

        <div className="shell site-header__bar">
          <Link to="/" className="wordmark wordmark--lockup">
            <img src="/brand/logo-full-flat.png" alt="JK Automations" width={73} height={40} />
          </Link>

          <nav className="site-nav" aria-label="Main">
            {NAV.map((item) => (
              <Link key={item.to} to={item.to} className={isActive(item.to) ? 'is-active' : undefined}>
                {item.label}
                {isActive(item.to) && (
                  <motion.span
                    className="nav__underline"
                    layoutId="nav-underline"
                    transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  />
                )}
              </Link>
            ))}
          </nav>

          <span className="header-cta">{cta('header')}</span>

          <button
            type="button"
            className="menu-button"
            aria-expanded={open}
            aria-controls="site-drawer"
            onClick={() => setOpen(true)}
          >
            <span className="menu-button__glyph" aria-hidden="true" />
            Menu
          </button>
        </div>
      </header>

      {/*
        Rendered outside <header>: that element's backdrop-filter (for the
        blurred sticky bar) establishes a containing block for fixed-position
        descendants, which would collapse the drawer's height to the header's
        own ~68px instead of the full viewport.
      */}
      <Drawer open={open} onClose={() => setOpen(false)} nav={NAV} isActive={isActive} />
    </>
  );
};

export default React.memo(Header);
